import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import * as XLSX from "xlsx";
import moment from "moment";
import { useHistory } from "react-router-dom";
import {
  ArrowBack,
  ArrowForward,
  CancelOutlined,
  CheckCircleOutline,
  DescriptionOutlined,
  FileDownloadOutlined,
  FileUploadOutlined,
  Replay,
  Search,
  StorageOutlined,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { GlobalFieldLabel, globalTextFieldSx, sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import {
  StatCard,
  designColors,
  designRadii,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  IMPORT_ACCEPT,
  IMPORT_FIELDS,
  IMPORT_GUIDELINES,
  IMPORT_MAX_BYTES,
  autoMapColumns,
  checkImportRow,
  importTemplateRows,
  missingRequiredFields,
  parseDate,
  splitSheetRows,
  validateImportRows,
} from "./shiftImportData";
import { BASIS_OPTIONS, DAY_TYPES } from "./shiftEarningsData";
import { importShiftEarnings, loadCompanyPaySchedules, validateShiftImport } from "./shiftEarningsApi";
import { applyServerChecks, convertDateTimeCells } from "../Shared/importMapping";
import { formatDate } from "../Shared/formatters";
import { WizardStepper } from "../Shared/WizardStepper";
import { DateField, SelectField, TextField, toOptions } from "../Shared/fieldInputs";
import { Field, FormSection, panelBodySx } from "../Shared/formUi";

// Payroll > Pay Inputs > Shift Earnings > Import: Upload File -> Map Columns
// -> Preview & Import (same layout as the Overtime import). The file is read
// in the browser with `xlsx`; rows without errors are sent with the chosen
// payroll cycle to POST payroll/earning/shift/import/, and the server's
// per-row result is shown afterwards. The payroll cycle is picked on Upload
// File; Map Columns > Next checks the rows against it with
// POST payroll/earning/shift/import/validate/ (nothing saved), so rows the
// server would refuse show as error rows and are not sent.

const STEPS = ["Upload File", "Map Columns", "Preview & Import"];

const fieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: designColors.teal },
  "& .MuiInputBase-input": { fontSize: 14 },
};
const tableHeadSx = { ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" };
const cellSx = { ...sharedTableBodyCellSx, py: 1 };
const errorText = "#DC2626";

const formatBytes = (bytes) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const isBlankText = (value) => value === null || value === undefined || String(value).trim() === "";

const rawText = (value) => {
  if (value instanceof Date) return formatDate(moment(value).format("YYYY-MM-DD"));
  return value === null || value === undefined || value === "" ? "–" : String(value);
};

const downloadTemplate = () => {
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, XLSX.utils.aoa_to_sheet(importTemplateRows()), "Shift Earnings");
  XLSX.writeFile(book, "Shift Earnings Import Template.xlsx");
};

const readSheet = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = () => {
      try {
        // No cellDates: xlsx turns date cells into local-time Dates that land
        // a few seconds before midnight in IST (05 Oct read as 04 Oct). Date
        // cells are turned into "YYYY-MM-DD" text from the cell's own day
        // number instead. raw: CSV cells stay as typed (no US-order dates).
        const book = XLSX.read(reader.result, { type: "array", cellNF: true, raw: true });
        const sheet = convertDateTimeCells(book.Sheets[book.SheetNames[0]], XLSX.SSF);
        resolve(XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" }));
      } catch (error) {
        reject(new Error("This file could not be read. Use the template."));
      }
    };
    reader.readAsArrayBuffer(file);
  });

const Card = ({ title, helper, action, children, sx }) => (
  <Mui.Box sx={{ ...detailCardSx, ...sx }}>
    {title ? (
      <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
        <Mui.Box>
          <Mui.Typography sx={designType.sectionHeading}>{title}</Mui.Typography>
          {helper ? <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>{helper}</Mui.Typography> : null}
        </Mui.Box>
        {action}
      </Mui.Stack>
    ) : null}
    {children}
  </Mui.Box>
);

const FileCard = ({ file, onChange }) => (
  <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ ...detailCardSx, backgroundColor: designColors.slateBg }}>
    <Mui.Box sx={{ width: 40, height: 40, borderRadius: designRadii.iconButton, backgroundColor: "#E7F8EF", color: "#12B76A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <DescriptionOutlined />
    </Mui.Box>
    <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
      <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }} noWrap>{file.name}</Mui.Typography>
      <Mui.Typography sx={designType.captionText}>{file.rowCount.toLocaleString("en-IN")} rows · {formatBytes(file.size)}</Mui.Typography>
    </Mui.Box>
    {onChange ? <SecondaryButton onClick={onChange} sx={{ minHeight: 36, minWidth: 0 }}>Change File</SecondaryButton> : null}
  </Mui.Stack>
);

// ---- Step 1: Upload ----

const UploadStep = ({ file, error, onFile, cycleProps }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const pick = (files) => { if (files?.[0]) onFile(files[0]); };

  return (
    <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 1.5, alignItems: "stretch" }}>
      <Mui.Stack spacing={1.5} sx={{ minHeight: "100%" }}>
        <PayrollCycleCard {...cycleProps} />
        <Mui.Box
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") inputRef.current?.click(); }}
          onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => { event.preventDefault(); setDragging(false); pick(event.dataTransfer.files); }}
          sx={{
            ...detailCardSx,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            px: 3,
            textAlign: "center",
            cursor: "pointer",
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: dragging ? designColors.teal : designColors.slate300,
            backgroundColor: dragging ? designColors.tealTint : designColors.white,
            "&:hover": { borderColor: designColors.teal },
          }}
        >
          <input ref={inputRef} hidden type="file" accept={IMPORT_ACCEPT} onChange={(event) => { pick(event.target.files); event.target.value = ""; }} />
          <FileUploadOutlined sx={{ fontSize: 40, color: designColors.teal }} />
          <Mui.Typography sx={{ ...designType.sectionHeading, fontSize: 16, mt: 1.5 }}>Drag and drop your file here</Mui.Typography>
          <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>
            or <Mui.Box component="span" sx={{ color: designColors.tealDark, fontWeight: designWeights.semibold }}>click to browse</Mui.Box>
          </Mui.Typography>
          <Mui.Typography sx={{ ...designType.captionText, mt: 2 }}>Supported formats: Excel (.xlsx, .xls), CSV (.csv) · Max 5 MB</Mui.Typography>
          <Mui.Button
            startIcon={<FileDownloadOutlined />}
            onClick={(event) => { event.stopPropagation(); downloadTemplate(); }}
            sx={{ mt: 2, textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}
          >
            Download Shift Earnings Template
          </Mui.Button>
        </Mui.Box>
        {file ? <FileCard file={file} /> : null}
        {error ? <RequestInfoBanner tone="danger" title="Cannot use this file" body={error} /> : null}
      </Mui.Stack>

      <Mui.Stack spacing={1.5} sx={{ minHeight: "100%" }}>
        <RequestInfoBanner title="Template Information" body="Use the Shift Earnings template to import shift earnings for employees. The template includes the columns with examples." />
        <Card sx={{ flex: 1 }}>
          <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <DescriptionOutlined sx={{ fontSize: 20, color: designColors.tealDark }} />
            <Mui.Typography sx={designType.sectionHeading}>Shift Earnings Import Guidelines</Mui.Typography>
          </Mui.Stack>
          <Mui.Stack spacing={1}>
            {IMPORT_GUIDELINES.map((line, index) => (
              <Mui.Stack key={line} direction="row" spacing={1.25} alignItems="flex-start">
                <Mui.Box sx={{ ...designType.captionText, flexShrink: 0, width: 20, height: 20, borderRadius: "50%", backgroundColor: designColors.slate100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {index + 1}
                </Mui.Box>
                <Mui.Typography sx={designType.helperText}>{line}</Mui.Typography>
              </Mui.Stack>
            ))}
          </Mui.Stack>
        </Card>
      </Mui.Stack>
    </Mui.Box>
  );
};

// The API takes one optional pay_schedule_id for the whole file, picked on
// the first step (Upload File). Blank = Automatic: the server picks each
// row's cycle from the employee + the row's date (same as Overtime). Map
// Columns > Next checks the rows against it before the preview.
const PayrollCycleCard = ({ cycle, onCycleChange, cycles, cyclesLoading, cyclesError, onRetryCycles }) => (
  <Card title="Payroll Cycle" helper="Leave as Automatic to use each row's own payroll cycle for its date.">
    {cyclesError ? (
      <Mui.Alert severity="warning" sx={{ mb: 1.5 }} action={<Mui.Button onClick={onRetryCycles}>Retry</Mui.Button>}>{cyclesError}</Mui.Alert>
    ) : null}
    <GlobalFieldLabel helperText="Payroll run the imported shift earnings are paid in.">Payroll Cycle</GlobalFieldLabel>
    <Mui.Box sx={{ width: "100%" }}>
      <SelectField
        value={cycle}
        onChange={onCycleChange}
        options={[{ value: "", label: "Automatic (each row's own cycle)" }, ...cycles]}
        disabled={cyclesLoading}
        placeholder={cyclesLoading ? "Loading payroll cycles..." : "Automatic (each row's own cycle)"}
      />
    </Mui.Box>
  </Card>
);

const AUTOMATIC_CYCLE_LABEL = "each row's own payroll cycle";

// ---- Step 2: Map columns ----

const MapStep = ({ file, mapping, onMappingChange, onReset, onChangeFile }) => {
  const missing = missingRequiredFields(mapping);
  const usedFields = new Set(Object.values(mapping));
  return (
    <Mui.Stack spacing={1.5}>
      <FileCard file={file} onChange={onChangeFile} />
      <Card
        title="Map File Columns"
        helper="Columns were matched automatically by header name — they can be in any order and renamed. Check each match and change it if needed."
        action={<Mui.Button startIcon={<Replay />} onClick={onReset} sx={{ textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}>Reset Mapping</Mui.Button>}
      >
        <Mui.TableContainer sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                <Mui.TableCell sx={tableHeadSx}>File Column (from Excel)</Mui.TableCell>
                <Mui.TableCell sx={tableHeadSx}>Map to System Field</Mui.TableCell>
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {file.headers.map((header, index) => {
                const fieldKey = mapping[index] ?? "";
                return (
                  <Mui.TableRow key={`${header}-${index}`}>
                    {/* Required marks only show on the system fields (next column). */}
                    <Mui.TableCell sx={{ ...cellSx, width: "45%" }}>
                      {header || `Column ${index + 1}`}
                    </Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      {/* A field already mapped to another column is left out. */}
                      <SelectField
                        value={fieldKey}
                        onChange={(value) => onMappingChange(index, value)}
                        options={[
                          { value: "", label: "Do not import" },
                          ...IMPORT_FIELDS.filter((option) => !usedFields.has(option.key) || option.key === fieldKey).map((option) => ({
                            value: option.key,
                            label: `${option.label}${option.required ? " *" : ""}`,
                          })),
                        ]}
                        placeholder="Do not import"
                      />
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {missing.length ? (
          <Mui.Box sx={{ mt: 2 }}>
            <RequestInfoBanner tone="warning" title="Required fields not mapped" body={`Map a column to: ${missing.map((item) => item.label).join(", ")}.`} />
          </Mui.Box>
        ) : null}
      </Card>
    </Mui.Stack>
  );
};

// ---- Step 3: Preview & Import ----

const PREVIEW_TABS = [
  { key: "all", label: "All Rows" },
  { key: "valid", label: "Valid Rows" },
  { key: "error", label: "Error Rows" },
];

const TabButtons = ({ tabs, tab, counts, onChange }) => (
  <Mui.Stack direction="row" spacing={1} sx={{ overflowX: "auto" }} role="tablist">
    {tabs.map((item) => {
      const active = item.key === tab;
      return (
        <Mui.ButtonBase
          key={item.key}
          role="tab"
          aria-selected={active}
          onClick={() => onChange(item.key)}
          sx={{ px: 1.5, py: 0.75, flexShrink: 0, borderRadius: designRadii.control, border: `1px solid ${active ? designColors.tealDark : designColors.slate200}`, backgroundColor: active ? designColors.tealTint : designColors.white }}
        >
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: active ? designWeights.semibold : designWeights.medium, color: active ? designColors.tealDark : designColors.slate700, whiteSpace: "nowrap" }}>
            {item.label} ({counts[item.key].toLocaleString("en-IN")})
          </Mui.Typography>
        </Mui.ButtonBase>
      );
    })}
  </Mui.Stack>
);

const usePaged = (items, resetKey) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  useEffect(() => setPage(0), [resetKey]);
  return {
    pageItems: items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    footer: (
      <Mui.Box sx={{ px: 2, py: 1, borderTop: `1px solid ${designColors.slate200}` }}>
        {paginationFooter({
          page,
          rowsPerPage,
          total: items.length,
          itemLabel: "rows",
          onPageChange: setPage,
          onRowsPerPageChange: (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); },
          rowsPerPageOptions,
        })}
      </Mui.Box>
    ),
  };
};

// Error rows are tinted red; clicking one opens the Edit panel (RowFixPanel)
// to correct it and check it again.
const errorRowSx = {
  cursor: "pointer",
  backgroundColor: "#FEF2F2",
  "&:hover": { backgroundColor: "#FEE2E2" },
  "&:focus-visible": { outline: `2px solid ${errorText}`, outlineOffset: -2 },
};

const PreviewStep = ({ results, cycleName, onFixRow }) => {
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const valid = results.filter((result) => result.record);
  const invalid = results.filter((result) => !result.record);
  const counts = { all: results.length, valid: valid.length, error: invalid.length };

  const shown = useMemo(() => {
    const base = tab === "valid" ? valid : tab === "error" ? invalid : results;
    const needle = query.trim().toLowerCase();
    return needle ? base.filter((result) => Object.values(result.raw).some((value) => rawText(value).toLowerCase().includes(needle))) : base;
  }, [tab, query, results, valid, invalid]);
  const { pageItems, footer } = usePaged(shown, `${tab}:${query}`);

  return (
    <Mui.Stack spacing={1.5}>
      <Card title="Summary" helper={`Rows are imported into ${cycleName}. Go back to Upload File to change the payroll cycle.`}>
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
          {/* Cards pick the rows shown below, same as the tabs. */}
          <StatCard icon={StorageOutlined} title="Total Rows" value={counts.all.toLocaleString("en-IN")} accent="#12B76A" tint="#E7F8EF" onClick={() => setTab("all")} isActive={tab === "all"} activeBorder />
          <StatCard icon={CheckCircleOutline} title="Valid Rows" value={counts.valid.toLocaleString("en-IN")} accent={designColors.tealDark} tint={designColors.tealTint} onClick={() => setTab("valid")} isActive={tab === "valid"} activeBorder />
          <StatCard icon={CancelOutlined} title="Error Rows" value={counts.error.toLocaleString("en-IN")} accent={errorText} tint="#FEF2F2" onClick={() => setTab("error")} isActive={tab === "error"} activeBorder />
        </Mui.Box>
        {invalid.length ? (
          <Mui.Box sx={{ mt: 1.5 }}>
            <RequestInfoBanner tone="warning" body={`${invalid.length.toLocaleString("en-IN")} row(s) have errors and will not be imported. Click a red row to correct it, or import only the valid rows.`} />
          </Mui.Box>
        ) : null}
      </Card>

      <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
        <Mui.Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ p: 2 }}>
          <TabButtons tabs={PREVIEW_TABS} tab={tab} counts={counts} onChange={setTab} />
          <Mui.TextField
            size="small"
            placeholder="Search..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ ...fieldSx, width: { xs: "100%", md: 260 } }}
            InputProps={{ startAdornment: <Mui.InputAdornment position="start"><Search sx={{ fontSize: 18, color: designColors.slate400 }} /></Mui.InputAdornment> }}
          />
        </Mui.Stack>
        <Mui.TableContainer sx={{ overflowX: "auto" }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                <Mui.TableCell sx={tableHeadSx}>Row</Mui.TableCell>
                {IMPORT_FIELDS.map((field) => <Mui.TableCell key={field.key} sx={tableHeadSx}>{field.label}</Mui.TableCell>)}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {pageItems.length === 0 ? (
                <Mui.TableRow>
                  <Mui.TableCell colSpan={IMPORT_FIELDS.length + 1}sx={{ ...cellSx, textAlign: "center", py: 4 }}>No rows to show.</Mui.TableCell>
                </Mui.TableRow>
              ) : (
                pageItems.map((result) => (
                  <Mui.TableRow
                    key={result.rowNumber}
                    {...(result.record
                      ? {}
                      : {
                          sx: errorRowSx,
                          tabIndex: 0,
                          title: "Click to correct this row",
                          onClick: () => onFixRow(result.rowNumber),
                          onKeyDown: (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onFixRow(result.rowNumber); } },
                        })}
                  >
                    <Mui.TableCell sx={cellSx}>{result.rowNumber}</Mui.TableCell>
                    {IMPORT_FIELDS.map((field) => (
                      <Mui.TableCell key={field.key} sx={cellSx}>
                        {result.record && field.key === "date" ? formatDate(result.record.date) : rawText(result.raw[field.key])}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                ))
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {footer}
      </Mui.Box>
    </Mui.Stack>
  );
};

// ---- Edit an error row (opened from the preview) ----

const textOf = (value) => (isBlankText(value) ? "" : String(value).trim());
const normalizeText = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Panel form values from the row's file values. Values the pickers can't
// show (an unknown basis, a date that can't be read) start blank so the
// user picks a valid one; the error banner still names the original value.
const rowToFixForm = (raw) => ({
  employee_id: textOf(raw.employee_id),
  date: isBlankText(raw.date) ? "" : parseDate(raw.date) || "",
  earning_type: DAY_TYPES.find((option) => normalizeText(option) === normalizeText(raw.earning_type)) || "",
  shift: textOf(raw.shift),
  basis: BASIS_OPTIONS.find((option) => [option.value, option.label].some((text) => normalizeText(text) === normalizeText(raw.basis)))?.value || "",
  quantity: textOf(raw.quantity),
  rate: textOf(raw.rate),
  factor: textOf(raw.factor),
});

const DAY_TYPE_OPTIONS = [{ value: "", label: "Not applicable" }, ...toOptions(DAY_TYPES)];

// Side panel over the import: the row's errors in a red box on top, its
// values below to correct, then Re-Verify runs the same browser check and
// the server's validate API for this row. A row that passes turns valid and
// the panel closes; otherwise the new errors show.
const RowFixPanel = ({ row, onClose, onVerify }) => {
  const [form, setForm] = useState(() => rowToFixForm(row.raw));
  const [errors, setErrors] = useState(row.errors);
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const hoursBasis = form.basis === "hours";

  const verify = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = await onVerify(row.rowNumber, form);
      if (next?.record) onClose();
      else if (next) setErrors(next.errors);
    } catch (error) {
      setErrors([`Could not check this row: ${error.message || "please try again."}`]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlobalSidePanel
      open
      onClose={busy ? undefined : onClose}
      hideExpand
      title="Edit Shift Earning Row"
      subtitle={`Row ${row.rowNumber}. Correct the values and re-verify.`}
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={onClose}
          primaryLabel={busy ? "Verifying..." : "Re-Verify"}
          primaryProps={{ disabled: busy, loading: busy, onClick: verify }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        {errors.length ? (
          <RequestInfoBanner
            tone="danger"
            title={errors.length > 1 ? `${errors.length} errors in this row` : "Error in this row"}
            body={errors.map((message) => (
              <Mui.Box key={message} component="span" sx={{ display: "block" }}>• {message}</Mui.Box>
            ))}
          />
        ) : null}
        <FormSection title="Shift Earning" info="Values from the file. Amount is calculated by the system.">
          <Field label="Employee ID *" helper="Employee code, as in the employee records." wide>
            <TextField value={form.employee_id} onChange={set("employee_id")} placeholder="e.g. EMP001" />
          </Field>
          <Field label="Date *" helper="Day the shift was worked.">
            <DateField value={form.date} onChange={set("date")} />
          </Field>
          <Field label="Day Type" helper="Kind of day. Optional.">
            <SelectField value={form.earning_type} onChange={set("earning_type")} options={DAY_TYPE_OPTIONS} placeholder="Not applicable" />
          </Field>
          <Field label="Shift" helper="Shift worked that day. Optional.">
            <TextField value={form.shift} onChange={set("shift")} placeholder="Shift name (optional)" />
          </Field>
          <Field label="Basis *" helper="Paid per shift or per hour.">
            <SelectField value={form.basis} onChange={set("basis")} options={BASIS_OPTIONS} placeholder="Shift or Hours" />
          </Field>
          <Field label="Credited Qty *" helper={hoursBasis ? "Hours worked, as a number or HH:MM." : "Number of shifts credited."}>
            <TextField value={form.quantity} onChange={set("quantity")} placeholder={hoursBasis ? "e.g. 08:30" : "e.g. 1"} />
          </Field>
          <Field label="Rate (per unit) *" helper="Amount paid per shift or per hour.">
            <TextField value={form.rate} onChange={set("rate")} placeholder="e.g. 100" />
          </Field>
          <Field label="Factor" helper="Multiplier on the quantity, e.g. 1.5. Blank = 1." wide>
            <TextField value={form.factor} onChange={set("factor")} placeholder="Blank = 1" />
          </Field>
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---- Result (server's answer per row) ----

const RESULT_TABS = [
  { key: "all", label: "All Rows" },
  { key: "success", label: "Imported" },
  { key: "failed", label: "Failed" },
];

const ResultStep = ({ result, cycleName }) => {
  const [tab, setTab] = useState(result.failed ? "failed" : "all");
  const rows = result.rows;
  const counts = { all: rows.length, success: rows.filter((row) => row.status === "success").length, failed: rows.filter((row) => row.status !== "success").length };
  const shown = tab === "all" ? rows : rows.filter((row) => (tab === "success" ? row.status === "success" : row.status !== "success"));
  const { pageItems, footer } = usePaged(shown, tab);

  return (
    <Mui.Stack spacing={1.5}>
      <Card title={result.failed ? "Import Finished With Errors" : "Import Complete"} helper={`Imported into ${cycleName}.`}>
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
          <StatCard icon={StorageOutlined} title="Total Records" value={Number(result.total ?? rows.length).toLocaleString("en-IN")} accent="#12B76A" tint="#E7F8EF" onClick={() => setTab("all")} isActive={tab === "all"} activeBorder />
          <StatCard icon={CheckCircleOutline} title="Imported" value={Number(result.success ?? counts.success).toLocaleString("en-IN")} accent={designColors.tealDark} tint={designColors.tealTint} onClick={() => setTab("success")} isActive={tab === "success"} activeBorder />
          <StatCard icon={CancelOutlined} title="Failed" value={Number(result.failed ?? counts.failed).toLocaleString("en-IN")} accent={errorText} tint="#FEF2F2" onClick={() => setTab("failed")} isActive={tab === "failed"} activeBorder />
        </Mui.Box>
      </Card>
      <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
        <Mui.Box sx={{ p: 2 }}>
          <TabButtons tabs={RESULT_TABS} tab={tab} counts={counts} onChange={setTab} />
        </Mui.Box>
        <Mui.TableContainer sx={{ overflowX: "auto" }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                {["Employee ID", "Employee", "Date", "Status", "Message"].map((label) => <Mui.TableCell key={label} sx={tableHeadSx}>{label}</Mui.TableCell>)}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {pageItems.length === 0 ? (
                <Mui.TableRow>
                  <Mui.TableCell colSpan={5} sx={{ ...cellSx, textAlign: "center", py: 4 }}>No rows to show.</Mui.TableCell>
                </Mui.TableRow>
              ) : (
                pageItems.map((row, index) => {
                  const ok = row.status === "success";
                  return (
                    <Mui.TableRow key={`${row.employee_id}-${row.date}-${index}`}>
                      <Mui.TableCell sx={cellSx}>{rawText(row.employee_id)}</Mui.TableCell>
                      <Mui.TableCell sx={cellSx}>{rawText(row.employee_name)}</Mui.TableCell>
                      <Mui.TableCell sx={cellSx}>{row.date ? formatDate(row.date) : "–"}</Mui.TableCell>
                      <Mui.TableCell sx={{ ...cellSx, fontWeight: designWeights.semibold, color: ok ? designColors.tealDark : errorText }}>{ok ? "Imported" : "Failed"}</Mui.TableCell>
                      <Mui.TableCell sx={{ ...cellSx, whiteSpace: "normal", color: ok ? undefined : errorText }}>{rawText(row.message)}</Mui.TableCell>
                    </Mui.TableRow>
                  );
                })
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {footer}
      </Mui.Box>
    </Mui.Stack>
  );
};

// ---- Wizard ----

// onImported(result) runs after the server answers, so the list can reload.
export const ShiftEarningImport = ({ open, onClose, onImported, domain, companyId }) => {
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [mapping, setMapping] = useState({});
  const [cycle, setCycle] = useState("");
  const [cycles, setCycles] = useState([]);
  const [cyclesLoading, setCyclesLoading] = useState(false);
  const [cyclesError, setCyclesError] = useState("");
  const [cyclesRetry, setCyclesRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [result, setResult] = useState(null);
  // Every file row after the browser check and the server's validate API
  // ({ rowNumber, raw, errors, record }). Rows corrected in the Edit panel
  // are replaced here one at a time.
  const [results, setResults] = useState([]);
  const [validating, setValidating] = useState(false);
  const [validateError, setValidateError] = useState("");
  const validateRef = useRef(null);
  // Row number of the error row open in the Edit panel.
  const [fixRowNumber, setFixRowNumber] = useState(null);
  const fixRow = fixRowNumber == null ? null : results.find((item) => item.rowNumber === fixRowNumber) || null;

  useEffect(() => {
    if (!open) return undefined;
    setStep(0); setFile(null); setFileError(""); setMapping({});
    setCycle(""); setSaving(false); setSaveError(""); setResult(null);
    setResults([]); setValidateError(""); setFixRowNumber(null);
    return () => validateRef.current?.abort();
  }, [open]);

  // Payroll cycles load once the panel opens (picked on Upload File).
  useEffect(() => {
    if (!open || !companyId) return undefined;
    const controller = new AbortController();
    setCyclesLoading(true); setCyclesError("");
    loadCompanyPaySchedules(domain, companyId, controller.signal)
      .then((list) => { if (!controller.signal.aborted) setCycles(list); })
      .catch((error) => { if (!controller.signal.aborted) { setCycles([]); setCyclesError(error.message); } })
      .finally(() => { if (!controller.signal.aborted) setCyclesLoading(false); });
    return () => controller.abort();
  }, [open, domain, companyId, cyclesRetry]);

  const records = results.filter((item) => item.record).map(({ record: { payable_amount: _amount, pay_schedule_name: _cycle, ...record } }) => record);

  // Sends the rows that passed the browser check to the validate API (with
  // the payroll cycle once one is picked). Returns the checked rows with the
  // server's answer applied, or null when the server could not be reached.
  const validateRows = async (rowsChecked, payScheduleId) => {
    validateRef.current?.abort();
    const toCheck = rowsChecked.filter((item) => item.record).map((item) => item.record);
    setValidateError("");
    if (!toCheck.length) return rowsChecked;
    const controller = new AbortController();
    validateRef.current = controller;
    setValidating(true);
    try {
      const data = await validateShiftImport(domain, payScheduleId, toCheck, controller.signal);
      if (controller.signal.aborted) return null;
      return applyServerChecks(rowsChecked, data.rows, "record");
    } catch (error) {
      if (!controller.signal.aborted) setValidateError(error.message);
      return null;
    } finally {
      if (!controller.signal.aborted) setValidating(false);
    }
  };

  // Map Columns > Next: browser check, then the server check, then preview.
  const handleMapNext = async () => {
    const checkedRows = await validateRows(validateImportRows(file.rows, mapping), cycle);
    if (!checkedRows) return;
    setResults(checkedRows);
    setStep(2);
  };

  // Edit panel > Re-Verify: the corrected values go through the same browser
  // check, then (if that passes) the validate API for this one row. The row
  // is replaced in the preview either way, so the table shows the new values.
  // Throws when the server can't be reached (the panel shows it).
  const verifyRow = async (rowNumber, raw) => {
    let next = checkImportRow(rowNumber, raw);
    if (next.record) {
      const data = await validateShiftImport(domain, cycle, [next.record]);
      next = applyServerChecks([next], data.rows, "record")[0];
    }
    setResults((current) => current.map((item) => (item.rowNumber === rowNumber ? next : item)));
    return next;
  };

  // The cycle is picked on Upload File; Map Columns > Next checks the rows against it
  // (each date must be inside it), so nothing is validated here.
  const handleCycleChange = (value) => {
    setCycle(value);
    setValidateError("");
  };
  const cycleName = cycle ? cycles.find((option) => option.value === cycle)?.label || cycle : AUTOMATIC_CYCLE_LABEL;

  const handleFile = async (picked) => {
    setFileError("");
    const extension = picked.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(extension)) {
      setFile(null);
      setFileError("Unsupported file type. Upload an Excel (.xlsx, .xls) or CSV (.csv) file.");
      return;
    }
    if (picked.size > IMPORT_MAX_BYTES) {
      setFile(null);
      setFileError("The file is larger than 5 MB.");
      return;
    }
    try {
      const { headers, rows } = splitSheetRows(await readSheet(picked));
      if (!headers.length || !rows.length) {
        setFile(null);
        setFileError("The file has no data rows under the header row.");
        return;
      }
      setFile({ name: picked.name, size: picked.size, headers, rows, rowCount: rows.length });
      setMapping(autoMapColumns(headers));
    } catch (error) {
      setFile(null);
      setFileError(error.message);
    }
  };

  const runImport = async () => {
    if (saving || !records.length) return;
    setSaving(true); setSaveError("");
    try {
      const answer = await importShiftEarnings(domain, cycle, records);
      setResult(answer);
      onImported?.(answer);
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const canContinue = (step === 0 && Boolean(file)) || (step === 1 && missingRequiredFields(mapping).length === 0);

  const footer = result ? (
    <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
      <PrimaryButton onClick={onClose}>Done</PrimaryButton>
    </Mui.Stack>
  ) : (
    <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ width: "100%" }}>
      <SecondaryButton disabled={saving || validating} onClick={onClose}>Cancel</SecondaryButton>
      <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
        {step > 0 ? <SecondaryButton disabled={saving || validating} startIcon={<ArrowBack />} onClick={() => setStep(step - 1)}>Back</SecondaryButton> : null}
        {step < 2 ? (
          <PrimaryButton
            endIcon={<ArrowForward />}
            disabled={!canContinue || validating}
            loading={step === 1 && validating}
            onClick={step === 1 ? handleMapNext : () => setStep(step + 1)}
          >
            {step === 1 && validating ? "Validating..." : "Next"}
          </PrimaryButton>
        ) : (
          <Mui.Tooltip arrow placement="top" slotProps={tooltipStyleProps} title={validating ? "Checking the rows against this payroll cycle..." : validateError ? "The rows could not be checked. Go back and press Next again." : !records.length ? "There are no valid rows to import." : ""}>
            <span>
              <PrimaryButton startIcon={<FileUploadOutlined />} disabled={!records.length || saving || validating || Boolean(validateError)} loading={saving || validating} onClick={runImport}>
                Import {records.length ? records.length.toLocaleString("en-IN") : ""}
              </PrimaryButton>
            </span>
          </Mui.Tooltip>
        )}
      </Mui.Stack>
    </Mui.Stack>
  );

  const subtitles = [
    "Choose the payroll cycle and upload a file to import shift earnings for employees.",
    "Map your file columns to the shift earnings fields.",
    "Review your data and import.",
  ];

  return (
    <GlobalSidePanel
      open={open}
      onClose={saving ? undefined : onClose}
      expanded={open}
      onToggleExpand={() => {}}
      hideExpand
      title="Import Shift Earnings"
      subtitle={result ? "Import result for each record." : subtitles[step]}
      // Payroll -> Payroll home; Shift Earnings -> back to the list (closes
      // this panel). Not while an import is being saved.
      breadcrumb={[
        { label: "Payroll", onClick: () => { if (!saving) history.push("/admin/payroll/setup"); } },
        "Pay Inputs",
        { label: "Shift Earnings", onClick: () => { if (!saving) onClose(); } },
        "Import",
      ]}
      bodySx={{ backgroundColor: designColors.pageBg, p: { xs: 1.5, sm: 2 } }}
      footer={footer}
    >
      <Mui.Stack spacing={1.5}>
        {result ? (
          <ResultStep result={result} cycleName={cycleName} />
        ) : (
          <>
            <Mui.Box sx={{ ...detailCardSx, py: 1 }}>
              <WizardStepper steps={STEPS} step={step} />
            </Mui.Box>
            {step === 0 ? (
              <UploadStep
                file={file}
                error={fileError}
                onFile={handleFile}
                cycleProps={{
                  cycle,
                  onCycleChange: handleCycleChange,
                  cycles,
                  cyclesLoading,
                  cyclesError,
                  onRetryCycles: () => setCyclesRetry((n) => n + 1),
                }}
              />
            ) : step === 1 ? (
              <MapStep
                file={file}
                mapping={mapping}
                onMappingChange={(index, fieldKey) =>
                  setMapping((current) => {
                    const next = { ...current };
                    if (fieldKey) next[index] = fieldKey;
                    else delete next[index];
                    return next;
                  })
                }
                onReset={() => setMapping(autoMapColumns(file.headers))}
                onChangeFile={() => setStep(0)}
              />
            ) : (
              <PreviewStep results={results} cycleName={cycleName} onFixRow={setFixRowNumber} />
            )}
          </>
        )}
        {validateError && !result ? <RequestInfoBanner tone="danger" title="Could not validate the rows" body={validateError} /> : null}
        {/* Import errors sit at the bottom, next to the Import button. */}
        {saveError ? <RequestInfoBanner tone="danger" title="Could not import shift earnings" body={saveError} /> : null}
      </Mui.Stack>
      {/* key: a fresh form for each row opened. */}
      {fixRow && step === 2 && !result ? (
        <RowFixPanel key={fixRow.rowNumber} row={fixRow} onClose={() => setFixRowNumber(null)} onVerify={verifyRow} />
      ) : null}
    </GlobalSidePanel>
  );
};
